import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationDataLabel,
  AccumulationLegend,
  AccumulationTooltip,
  Inject,
  PieSeries,
  AccumulationAnnotation,
} from '@syncfusion/ej2-react-charts';
import { Box, Skeleton, Typography } from '@mui/material';
import { MoneyFormatv3 } from '../../../library/MoneyFormat';
import { useThemes } from '../../../components/Theme/theme';

// Constants
const INITIAL_RADIUS = 80;
const DECREMENT = 3;
const INSIDE_CHAR_MULTIPLIER = 2.5;
const INSIDE_CHAR_MIN = 15;
const INSIDE_CHAR_MAX = 35;
const BASE_FONT_SIZE = 12;

// Screen breakpoints
const BREAKPOINTS = {
  XS: 399,
  SM: { min: 400, max: 639 },
  MD: { min: 640, max: 767 },
  LG: { min: 768, max: 1023 },
  XL: { min: 1024, max: 1279 },
  XXL: { min: 1280, max: 1535 },
  XXXL: { min: 1536, max: 1799 },
  XXXXL: { min: 1800, max: 2159 },
  XXXXXL: { min: 2160, max: 2559 },
};

// Wrapping configuration constants
const WRAP_CONFIG = {
  LARGE_SCREEN: { threshold: 0.95, maxChars: null, breakRatio: 0.85, secondPartThreshold: 0.9 },
  XXXL_UPPER: { threshold: 0.9, maxChars: null, breakRatio: 0.8, secondPartThreshold: 0.85 },
  XXXL_LOWER: { threshold: 0.85, maxChars: null, breakRatio: 0.75, secondPartThreshold: 0.8 },
  XXL: { threshold: 0.85, maxChars: null, breakRatio: 0.75, secondPartThreshold: 0.8 },
  MEDIUM_SCREEN: { threshold: 0.85, maxChars: null, breakRatio: 0.75, secondPartThreshold: 0.8 },
  SMALL_SCREEN: { threshold: 0.8, maxChars: null, breakRatio: 0.7, secondPartThreshold: 0.75 },
};

// Utility functions
const calculateRadius = (index, initialRadius, decrement) => {
  return `${initialRadius - index * decrement}%`;
};

const calculateSum = (dataArray, groupNames) => {
  if (!dataArray?.length) return 0;
  return dataArray
    .filter((item) => groupNames.includes(item.group_name))
    .reduce((sum, item) => sum + (item.balance || 0), 0);
};

const calculateNetProfitLoss = (groupData) => {
  const GROUP_NAMES = {
    TRADING_INCOMES: 'Trading Incomes',
    DIRECT_INCOME: 'Direct Income',
    TRADING_EXPENSE: 'Trading Expense',
    DIRECT_EXPENSE: 'Direct Expense',
  };

  const netIncomeGroups = [GROUP_NAMES.TRADING_INCOMES, GROUP_NAMES.DIRECT_INCOME, 'Indirect Income'];
  const netExpenseGroups = [
    GROUP_NAMES.TRADING_EXPENSE,
    GROUP_NAMES.DIRECT_EXPENSE,
    'Indirect Expenses',
    'Depreciation & Amortisation',
    'Interest Expenses',
    'Tax Expenses',
  ];

  const stockData = groupData?.[0] || {};
  const closingStock = stockData.closingstockamt || 0;
  const openingStock = stockData.openingstockamt || 0;

  const netIncome = calculateSum(groupData, netIncomeGroups) + closingStock;
  const netExpense = calculateSum(groupData, netExpenseGroups) + openingStock;
  const netProfitLoss = netIncome - netExpense;

  return {
    netProfitLoss,
    isNetProfit: netProfitLoss > 0,
    isNetLoss: netProfitLoss < 0,
    netIncome,
    netExpense,
  };
};

const processGroupData = (groupData) => {
  if (!groupData || groupData.length === 0) return { innerChartData: [], outerChartData: [] };

  const { netProfitLoss, isNetProfit, isNetLoss } = calculateNetProfitLoss(groupData);

  const incomeCategories = {
    'Trading Incomes': 'Trading Incomes',
    'Direct Income': 'Direct Income',
    'Indirect Income': 'Indirect Income',
  };

  const expenseCategories = {
    'Trading Expense': 'Trading Expense',
    'Direct Expense': 'Direct Expense',
    'Salary, Benefits & Wage Expenses': 'Salary, Benefits & Wage Expenses',
    'General & Administrative Expenses': 'General & Administrative Expenses',
    'Advertising & Promotion': 'Advertising & Promotion',
    'Professional Services': 'Professional Services',
    'Depreciation & Amortisation': 'Depreciation & Amortisation',
    'Interest Expenses': 'Interest Expenses',
    'Tax Expenses': 'Tax Expenses',
  };

  // Process income data
  const incomeData = {};
  let totalIncome = 0;

  Object.keys(incomeCategories).forEach((category) => {
    const categoryData = groupData.find((item) => item.group_name === category);
    const amount = categoryData ? Math.abs(categoryData.balance) : 0;
    incomeData[category] = amount;
    totalIncome += amount;
  });

  if (isNetLoss && Math.abs(netProfitLoss) > 0) {
    incomeData['Net Loss'] = Math.abs(netProfitLoss);
    totalIncome += Math.abs(netProfitLoss);
  }

  const innerChartData = Object.keys(incomeData)
    .map((category) => {
      const amount = incomeData[category];
      const percentage = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;
      return { category, amount, percentage };
    })
    .filter((item) => item.amount > 0 && item.percentage > 0);

  // Adjust net loss percentage if needed
  const netLossItem = innerChartData.find((item) => item.category === 'Net Loss');
  if (netLossItem) {
    const otherItemsTotal = innerChartData
      .filter((item) => item.category !== 'Net Loss')
      .reduce((sum, item) => sum + parseFloat(item.percentage.toFixed(2)), 0);
    const currentTotal = parseFloat(otherItemsTotal.toFixed(2)) + parseFloat(netLossItem.percentage.toFixed(2));
    const difference = parseFloat((100 - currentTotal).toFixed(2));
    const targetPercentage = parseFloat((netLossItem.percentage + difference).toFixed(2));
    const ratioAdjustment = targetPercentage / netLossItem.percentage;

    netLossItem.amount = parseFloat((netLossItem.amount * ratioAdjustment).toFixed(2));
    netLossItem.percentage = targetPercentage;

    if (netLossItem.percentage < 0) {
      netLossItem.percentage = 0.01;
      netLossItem.amount = 0.01;
    }
  }

  // Process expense data
  const expenseData = {};
  let totalExpenses = 0;

  Object.keys(expenseCategories).forEach((category) => {
    const categoryData = groupData.find((item) => item.group_name === category);
    let amount = 0;

    if (category === 'Trading Expense') {
      const tradingExpenseData = categoryData;
      const purchaseData = groupData.find((item) => item.group_name === 'Purchases');

      if (tradingExpenseData) {
        const openingStock = tradingExpenseData.openingstockamt || 0;
        const purchase = purchaseData ? Math.abs(purchaseData.balance) : 0;
        const closingStock = tradingExpenseData.closingstockamt || 0;
        amount = Math.max(0, openingStock + purchase - closingStock);
      } else {
        amount = categoryData ? Math.abs(categoryData.balance) : 0;
      }
    } else {
      amount = categoryData ? Math.abs(categoryData.balance) : 0;
    }

    expenseData[category] = amount;
    totalExpenses += amount;
  });

  // Add indirect expense sub-groups
  const indirectExpenseSubGroups = groupData.filter(
    (item) =>
      item.group_path &&
      item.group_path.startsWith('/25/') &&
      item.group_name !== 'Indirect Expenses' &&
      item.group_name !== 'Indirect Income'
  );

  const processedCategories = Object.keys(expenseData);
  indirectExpenseSubGroups.forEach((item) => {
    if (!processedCategories.includes(item.group_name)) {
      const amount = Math.abs(item.balance);
      expenseData[item.group_name] = amount;
      totalExpenses += amount;
    }
  });

  if (isNetProfit && netProfitLoss > 0) {
    expenseData['Net Profit'] = netProfitLoss;
    totalExpenses += netProfitLoss;
  }

  const expenseEntries = Object.keys(expenseData)
    .map((category) => ({
      label: category,
      amount: expenseData[category],
      percentage: totalExpenses > 0 ? (expenseData[category] / totalExpenses) * 100 : 0,
    }))
    .filter((item) => {
      const hasValidAmount =
        item.amount > 0 || item.label === 'Trading Expense' || item.label === 'General & Administrative Expenses';
      const hasValidPercentage = item.percentage > 0;
      const isNotPurchases = item.label !== 'Purchases';
      return hasValidAmount && hasValidPercentage && isNotPurchases;
    })
    .sort((a, b) => b.amount - a.amount);

  // Process top 7 items and others
  const sortedExpenseEntries = [...expenseEntries].sort((a, b) => b.percentage - a.percentage);
  const top7Items = sortedExpenseEntries.slice(0, 7);
  const remainingItems = sortedExpenseEntries.slice(7);
  const othersItems = remainingItems.filter((item) => item.label !== 'Net Profit');
  const netProfitItem = remainingItems.find((item) => item.label === 'Net Profit');

  let processedExpenseEntries = [...top7Items];

  if (othersItems.length > 0) {
    processedExpenseEntries.push({
      label: 'Others',
      amount: othersItems.reduce((sum, item) => sum + item.amount, 0),
      percentage: othersItems.reduce((sum, item) => sum + item.percentage, 0),
      isOthers: true,
    });
  }

  if (netProfitItem) {
    processedExpenseEntries.push(netProfitItem);
  }

  // Adjust net profit percentage if needed
  const finalNetProfit = processedExpenseEntries.find((item) => item.label === 'Net Profit');
  if (finalNetProfit) {
    const otherItemsTotal = processedExpenseEntries
      .filter((item) => item.label !== 'Net Profit')
      .reduce((sum, item) => sum + parseFloat(item.percentage.toFixed(2)), 0);
    const currentTotal = parseFloat(otherItemsTotal.toFixed(2)) + parseFloat(finalNetProfit.percentage.toFixed(2));
    const difference = parseFloat((100 - currentTotal).toFixed(2));
    const targetPercentage = finalNetProfit.percentage + difference;
    const ratioAdjustment = targetPercentage / finalNetProfit.percentage;

    finalNetProfit.amount = parseFloat((finalNetProfit.amount * ratioAdjustment).toFixed(2));
    finalNetProfit.percentage = targetPercentage;

    if (finalNetProfit.percentage < 0) {
      finalNetProfit.percentage = 0.01;
    }
  }

  const outerChartData = processedExpenseEntries.map((item, index) => ({
    ...item,
    radius: calculateRadius(index, INITIAL_RADIUS, DECREMENT),
    isLargeSlice: item.percentage > 8,
  }));

  const hasValidData = innerChartData.length > 0 || outerChartData.length > 0;
  if (!hasValidData) {
    return { innerChartData: [], outerChartData: [] };
  }

  const allAmountsZero = [...innerChartData, ...outerChartData].every(
    (item) => item.amount === 0 || item.amount === undefined
  );

  if (allAmountsZero) {
    return { innerChartData: [], outerChartData: [] };
  }

  return { innerChartData, outerChartData };
};

const PieChart = ({ year, id, groupData, loading = true, title, isColumnLayout = false }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    // Ensure windowWidth is set immediately on mount to prevent label calculation issues
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
    }
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Screen size detection
  const screenSizes = useMemo(() => {
    const isXs = windowWidth <= BREAKPOINTS.XS;
    const isSm = windowWidth >= BREAKPOINTS.SM.min && windowWidth <= BREAKPOINTS.SM.max;
    const isMd = windowWidth >= BREAKPOINTS.MD.min && windowWidth <= BREAKPOINTS.MD.max;
    const isLg = windowWidth >= BREAKPOINTS.LG.min && windowWidth <= BREAKPOINTS.LG.max;
    const isXl = windowWidth >= BREAKPOINTS.XL.min && windowWidth <= BREAKPOINTS.XL.max;
    const is2XL = windowWidth >= BREAKPOINTS.XXL.min && windowWidth <= BREAKPOINTS.XXL.max;
    const is3XL = windowWidth >= BREAKPOINTS.XXXL.min && windowWidth <= BREAKPOINTS.XXXL.max;
    const is4XL = windowWidth >= BREAKPOINTS.XXXXL.min && windowWidth <= BREAKPOINTS.XXXXL.max;
    const is5XL = windowWidth >= BREAKPOINTS.XXXXXL.min && windowWidth <= BREAKPOINTS.XXXXXL.max;

    return { isXs, isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL };
  }, [windowWidth]);

  console.log({ windowWidth, ...screenSizes }); // not remove

  // Responsive configurations
  const chartHeight = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isMd) return '300px';
    if (isLg) return '500px';
    if (isXl) return '600px';
    if (is2XL) return '380px';
    if (is3XL) return '550px';
    if (is4XL) return '580px';
    if (is5XL) return '600px';
    return '620px';
  }, [screenSizes]);

  const dummyChartHeight = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isMd) return '470px';
    if (isLg) return '520px';
    if (isXl) return '620px';
    if (is2XL) return '400px';
    if (is3XL) return '580px';
    if (is4XL) return '600px';
    if (is5XL) return '720px';
    return '740px';
  }, [screenSizes]);

  const legendFontSize = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isMd || isLg) return '10px';
    if (isMd || isLg) return '11px';
    if (isXl) return '11.5px';
    if (is2XL || is4XL) return '12px';
    if (is3XL) return '13px';
    if (is5XL) return '15px';
    return '11px';
  }, [screenSizes]);

  const innerChartLabelFontSize = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isLg) return '10px';
    if (isMd || isXl) return '11px';
    if (is2XL || is4XL) return '12px';
    if (is3XL) return '13px';
    if (is5XL) return '14px';
    return '11px';
  }, [screenSizes]);

  const outerChartLabelFontSize = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isMd) return '8px';
    if (isLg || isXl) return '9px';
    if (is2XL || is3XL) return '10px';
    if (is4XL) return '11px';
    if (is5XL) return '12px';
    return '11px';
  }, [screenSizes]);

  const dummyChartLabelFontSize = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    if (isSm || isMd || isLg || isXl || is2XL || is3XL) return '10px';
    if (is4XL) return '11px';
    if (is5XL) return '12px';
    return '11px';
  }, [screenSizes]);

  const innerChartRadius = useMemo(() => {
    const { isXl, isLg } = screenSizes;
    return isXl || isLg ? '26%' : '28%';
  }, [screenSizes]);

  const formatPercentage = useCallback((value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '0%';
    if (Math.abs(numValue - 100) < 0.01) return '100%';
    if (Math.abs(numValue) < 0.01) return '0%';
    return `${numValue.toFixed(2)}%`;
  }, []);

  
 

 


  // Render text for chart labels
  const textRender = useCallback(
    (args) => {
      const percentageValue = args.point.percentage || 0;
      const percentage = percentageValue.toFixed(2);
      if (parseFloat(percentage) === 0) {
        args.text = '';
        return;
      }

      args.font.fontStyle = 'roboto';

      let label = args.point.x || '';
      const labelLength = label.length;
      const isInnerChart = args.series.name === 'Income';

      if (isInnerChart) {
        args.font.color = 'white';
        args.text = formatPercentage(percentageValue);
        args.font.size = innerChartLabelFontSize;
      } else {
        args.font.size = outerChartLabelFontSize;
        args.font.fontWeight = 'bold';

        const isLargeSlice = args.point.percentage > 8;
        const isMediumSlice = args.point.percentage <= 8 && args.point.percentage > 2;
        const shouldBeInside = isLargeSlice ? labelLength <= 27 : isMediumSlice ? labelLength <= 22 : false;
        const formattedPercentage = formatPercentage(percentageValue);

        if (shouldBeInside) {
          args.text = `${label}<br>${formattedPercentage}`;
        } else {
          let displayLabel = label;
          if (labelLength > 23) {
            const midPoint = Math.floor(labelLength / 2);
            const spaceBefore = label.lastIndexOf(' ', midPoint);
            const spaceAfter = label.indexOf(' ', midPoint);
            let breakPoint = midPoint;
            if (spaceBefore > labelLength * 0.3) {
              breakPoint = spaceBefore + 1;
            } else if (spaceAfter > 0 && spaceAfter < labelLength * 0.7) {
              breakPoint = spaceAfter + 1;
            }
            displayLabel = `${label.substring(0, breakPoint)}<br>${label.substring(breakPoint)}`;
          }
          args.text = `${displayLabel}<br>${formattedPercentage}`;
        }

        args.font.color = '#000000';
      }
    },
    [innerChartLabelFontSize, outerChartLabelFontSize, formatPercentage]
  );

  const tooltipRender = useCallback((args) => {
    const isInnerChart = args.series.name === 'Income';
    const chartType = isInnerChart ? 'Income' : 'Expense';
    args.headerText = `${chartType} Details`;
    args.text = `${args.point.x}: ${MoneyFormatv3(args.point.y)}`;
  }, []);

  const dummytextRender = useCallback(
    (args) => {
      args.font.fontWeight = 'bold';
      args.font.fontStyle = 'roboto';
      args.font.size = dummyChartLabelFontSize;
      args.font.opacity = 0.3;
      args.text = `${args.point.x}`;
    },
    [dummyChartLabelFontSize]
  );

  // Chart configurations
  const animationConfig = useMemo(
    () => ({
      enable: true,
      duration: 2000,
      delay: 500,
      easing: 'EaseOut',
    }),
    []
  );

  const marginConfig = useMemo(() => {
    const { is4XL, is5XL } = screenSizes;
    if (is4XL || is5XL) return { top: 35, bottom: 35, left: 35, right: 35 };
    return { top: 10, bottom: 10, left: 10, right: 10 };
  }, [screenSizes]);

  const datalabel = useMemo(
    () => ({
      visible: true,
      position: 'Inside',
      textWrap: 'Wrap',
      connectorStyle: { color: 'black', length: '5px' },
      name: 'text',
    }),
    []
  );

  const innerDatalabel = useMemo(
    () => ({
      visible: true,
      position: 'Inside',
      textWrap: 'Wrap',
      enableRotation: false,
      name: 'text',
      // Don't set maxWidth to allow full text visibility
      font: {
        size: innerChartLabelFontSize,
        fontWeight: 'bold',
        color: 'white',
      },
    }),
    [innerChartLabelFontSize]
  );

  const datalabelDummy = useMemo(
    () => ({
      visible: true,
      position: 'Inside',
      maxWidth: 100,
      textWrap: 'Wrap',
      connectorStyle: { length: '1px', width: 1 },
      name: 'text',
    }),
    []
  );

  const outerDataLabel = useMemo(() => {
    const { isSm, isMd, isLg, isXl, is2XL, is3XL, is4XL, is5XL } = screenSizes;
    // Increase connector length to prevent overlap and ensure visibility
    let connectorLength = '5px';
    if (isSm || isMd) connectorLength = '5px';
    else if (isLg || isXl) connectorLength = '5px';
    else if (is2XL) connectorLength = '8px';
    else if (is3XL) connectorLength = '20px';
    else if (is4XL || is5XL) connectorLength = '30px';
    return {
      ...datalabel,
      position: 'Outside',
      connectorStyle: { 
        color: 'black', 
        length: connectorLength,
        width: 1
      },
      textWrap: 'Wrap',
    };
  }, [datalabel, screenSizes]);

  const { theme: customTheme } = useThemes();

  const themeValues = useMemo(
    () => ({
      isDarkTheme: customTheme?.palette?.TmId?.id?.toString().includes('9'),
      textColor: customTheme?.palette?.TmId?.id?.toString().includes('9') ? '#ffff' : '#080000',
      noDataColor: customTheme?.palette?.TmId?.id?.toString().includes('9')
        ? 'rgba(255,255,255,0.6)'
        : 'rgba(8,0,0,0.6)',
    }),
    [customTheme?.palette?.TmId?.id]
  );

  const { innerChartData: rawInnerChartData, outerChartData } = useMemo(() => {
    return processGroupData(groupData);
  }, [groupData]);

  const paletteArrays = useMemo(() => {
    const paletteSetOne = ['#FDB033', '#ff7b00', '#ff8800', '#ff9500', '#FB8C00', '#FC9E1A', '#FDB033', '#FEC24C'];
    const paletteSetTwo = ['#911B7C', '#9D2B84', '#A93B8C', '#B54B94', '#C15B9C', '#CD6BA4', '#D97BAC', '#E58BB4'];
    const dummyPaletteSetTwo = ['#C88DBE', '#CE96C2', '#D49FC6', '#DAA8CA', '#E0B1CE', '#E6BAD2', '#EBBDD6', '#F1C6DA'];
    const dummyPaletteSetOne = ['#E8B980', '#E8BA82', '#E8BC85', '#FCC480', '#FDD09D', '#FED9B3', '#FEE5C0'];
    const innerPaletteSetOne = ['#f79d65', '#ff7b00', '#ff8800', '#f7b267', '#f79d65', '#DA70D6', '#741E71', '#AE388B'];
    const innerPaletteSetTwo = ['#cbdb47', '#fdc77b', '#d8572a', '#f6863d'];

    return {
      outerPalettes: id === 'chartdiv1' ? [...paletteSetOne].reverse() : [...paletteSetTwo].reverse(),
      innerPalettes: id === 'chartdiv1' ? [...innerPaletteSetTwo].reverse() : [...innerPaletteSetOne].reverse(),
      dummyPalettes: id === 'chartdiv1' ? [...dummyPaletteSetOne].reverse() : [...dummyPaletteSetTwo].reverse(),
    };
  }, [id]);

  const innerChartData = useMemo(() => {
    return rawInnerChartData.map((item, index) => {
      const oppositePalette = paletteArrays.innerPalettes || [];
      const percentageLabel = `${item.percentage.toFixed(2)}%`;
      const color = oppositePalette.length > 0 ? oppositePalette[index % oppositePalette.length] : '#999999';

      return {
        ...item,
        color,
        alphabetLabel: percentageLabel,
        legendLabel: String.fromCharCode(97 + index),
      };
    });
  }, [rawInnerChartData, paletteArrays.innerPalettes]);

  const innerTooltipConfig = useMemo(
    () => ({
      enable: false,
      format: '${point.x}: ${point.y}', // eslint-disable-line no-template-curly-in-string
      header: 'Income Details',
      enableAnimation: false,
    }),
    []
  );

  const outerTooltipConfig = useMemo(
    () => ({
      enable: true,
      format: '${point.x}: ${point.y}', // eslint-disable-line no-template-curly-in-string
      header: 'Expense Details',
      enableAnimation: false,
      fill: '#ffffff',
      textStyle: {
        color: '#000000',
        fontFamily: 'Roboto',
        fontWeight: 'normal',
        size: '14px',
      },
      border: {
        color: '#cccccc',
        width: 1,
      },
      enableShadow: false,
    }),
    []
  );

  const hiddenLegendSettings = useMemo(() => ({ visible: false }), []);

  const titleGradient = useMemo(
    () =>
      id === 'chartdiv1'
        ? 'linear-gradient(135deg, #FDB033 0%, #ff7b00 50%, #FB8C00 100%)'
        : 'linear-gradient(135deg, #911B7C 0%, #B54B94 50%, #D97BAC 100%)',
    [id]
  );

  const titleAccentColor = useMemo(() => (id === 'chartdiv1' ? '#FDB033' : '#911B7C'), [id]);

  return (
    <Box sx={{ borderRadius: 2, backgroundColor: 'transparent', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isColumnLayout ? 'flex-start' : id === 'chartdiv2' ? 'flex-end' : 'flex-start',
          justifyContent: 'flex-start',
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          mb: { xs: 0.5, sm: 1 },
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              position: 'relative',
              pb: 1,
              alignSelf: isColumnLayout ? 'flex-start' : id === 'chartdiv2' ? 'flex-end' : 'flex-start',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                <Box
                  sx={{
                    width: { xs: 10, sm: 12 },
                    height: { xs: 10, sm: 12 },
                    borderRadius: '50%',
                    background: titleGradient,
                    boxShadow: `0 2px 12px ${titleAccentColor}60, 0 0 0 4px ${titleAccentColor}15`,
                    flexShrink: 0,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: 4, sm: 5 },
                      height: { xs: 4, sm: 5 },
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      opacity: 0.9,
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: themeValues.textColor,
                    fontWeight: '600',
                    fontSize: { xs: '11px', sm: '12px', md: '15px' },
                    letterSpacing: '0.01em',
                    position: 'relative',
                    lineHeight: 1.4,
                  }}
                >
                  {title || 'FY: Not selected'}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  height: '2px',
                  width: { xs: '80px', sm: '100px', md: '120px' },
                  ml: { xs: 1, sm: 3 },
                  mt: -0.2,
                  alignSelf: isColumnLayout ? 'flex-start' : id === 'chartdiv2' ? 'flex-end' : 'flex-start',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, ${titleAccentColor} 0%, transparent 100%)`,
                    borderRadius: '2px',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -8,
                    top: -2,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: titleAccentColor,
                    boxShadow: `0 0 8px ${titleAccentColor}80`,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2, mb: 2, minHeight: '30px', display: 'flex', alignItems: 'flex-start' }}>
        {innerChartData?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 1.5, md: 1 },
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            {innerChartData.map((item, idx) => (
              <Box
                key={`income-${idx}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 10, sm: 12, md: 14 },
                    height: { xs: 10, sm: 12, md: 14 },
                    borderRadius: '50%',
                    bgcolor: item.color || '#999',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: themeValues.textColor,
                    fontSize: legendFontSize,
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.category}{' '}
                  <Box component="span" sx={{ opacity: 0.7, fontWeight: '600' }}>
                    ({formatPercentage(item.percentage)})
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
        {innerChartData?.length > 0 || outerChartData?.length > 0 ? (
          <Box
            sx={{
              position: 'relative',
              height: chartHeight,
              width: '100%',
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}>
              <AccumulationChartComponent
                key={`${id}-outer-${windowWidth}-${screenSizes.is2XL}`}
                id={`${id}-outer`}
                background={'transparent'}
                enableAnimation={true}
                height={chartHeight}
                width={'100%'}
                tooltip={outerTooltipConfig}
                textRender={textRender}
                tooltipRender={tooltipRender}
                legendSettings={hiddenLegendSettings}
                enableSmartLabels={true}
                margin={marginConfig}
                style={{ pointerEvents: 'auto' }}
              >
                <Inject
                  services={[PieSeries, AccumulationLegend, AccumulationTooltip, AccumulationDataLabel, AccumulationAnnotation]}
                />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    name={'Expense'}
                    type={'Pie'}
                    dataSource={outerChartData}
                    xName={'label'}
                    yName={'amount'}
                    innerRadius={'32%'}
                    radius="radius"
                    explode={true}
                    explodeOffset={'6%'}
                    startAngle={90}
                    endAngle={450}
                    palettes={paletteArrays.outerPalettes}
                    border={{ width: 1, color: '#ffffff' }}
                    dataLabel={{ ...outerDataLabel, visible: true }}
                    animation={animationConfig}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            </Box>

            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
              <AccumulationChartComponent
                key={`${id}-inner-${windowWidth}-${screenSizes.is2XL}`}
                id={`${id}-inner`}
                background={'transparent'}
                enableAnimation={true}
                enableSmartLabels={true}
                height={chartHeight}
                width={'100%'}
                tooltip={innerTooltipConfig}
                textRender={textRender}
                legendSettings={hiddenLegendSettings}
                margin={marginConfig}
                style={{ pointerEvents: 'none' }}
              >
                <Inject
                  services={[PieSeries, AccumulationLegend, AccumulationTooltip, AccumulationDataLabel, AccumulationAnnotation]}
                />
                <AccumulationSeriesCollectionDirective>
                  <AccumulationSeriesDirective
                    name={'Income'}
                    type={'Pie'}
                    dataSource={innerChartData}
                    xName={'category'}
                    yName={'amount'}
                    radius={innerChartRadius}
                    explode={false}
                    pointColorMapping={'color'}
                    startAngle={90}
                    endAngle={450}
                    border={{ width: 1, color: '#ffffff' }}
                    dataLabel={{ ...innerDatalabel, visible: true }}
                    animation={animationConfig}
                  />
                </AccumulationSeriesCollectionDirective>
              </AccumulationChartComponent>
            </Box>
          </Box>
        ) : (
          <>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: { xs: 400, sm: 500, md: 600 },
                }}
              >
                <Skeleton
                  variant="circular"
                  width={{ xs: 200, sm: 250, md: 300 }}
                  height={{ xs: 200, sm: 250, md: 300 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  height: dummyChartHeight,
                  width: '100%',
                }}
              >
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}>
                  <AccumulationChartComponent
                    id={`${id}-dummy`}
                    background={'transparent'}
                    enableAnimation={true}
                    height={dummyChartHeight}
                    width={'100%'}
                    legendSettings={hiddenLegendSettings}
                    textRender={dummytextRender}
                    margin={marginConfig}
                  >
                    <Inject
                      services={[
                        PieSeries,
                        AccumulationLegend,
                        AccumulationTooltip,
                        AccumulationDataLabel,
                        AccumulationAnnotation,
                      ]}
                    />
                    <AccumulationSeriesCollectionDirective>
                      <AccumulationSeriesDirective
                        name={'No Data'}
                        type={'Pie'}
                        dataSource={[
                          { category: 'Trading expenses', amount: 150, radius: calculateRadius(0, 80, DECREMENT) },
                          { category: 'Direct Expenses', amount: 85, radius: calculateRadius(1, 80, DECREMENT) },
                          {
                            category: 'Salary, Benefits & Wage Expenses',
                            amount: 80,
                            radius: calculateRadius(2, 80, DECREMENT),
                          },
                          {
                            category: 'General & Administrative Expenses',
                            amount: 75,
                            radius: calculateRadius(3, 80, DECREMENT),
                          },
                          { category: 'Depreciation & Amortisation', amount: 60, radius: calculateRadius(4, 80, DECREMENT) },
                          { category: 'Interest Expenses', amount: 45, radius: calculateRadius(5, 80, DECREMENT) },
                          { category: 'Tax Expenses', amount: 30, radius: calculateRadius(6, 80, DECREMENT) },
                        ]}
                        xName={'category'}
                        yName={'amount'}
                        innerRadius={'28%'}
                        radius={'radius'}
                        palettes={paletteArrays.dummyPalettes}
                        border={{ width: 1, color: '#ffffff' }}
                        dataLabel={datalabelDummy}
                        animation={animationConfig}
                      />
                    </AccumulationSeriesCollectionDirective>
                  </AccumulationChartComponent>
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 2,
                    background: themeValues.isDarkTheme
                      ? 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 70%, transparent 100%)'
                      : 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 70%, transparent 100%)',
                    borderRadius: '50%',
                  }}
                >
                  <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 2, sm: 2.5 }, opacity: 90 }}>
                    <Typography
                      sx={{
                        color: themeValues.noDataColor,
                        fontSize: { xs: '12px', sm: '15px', md: '18px' },
                        fontWeight: '700',
                        textShadow: themeValues.isDarkTheme
                          ? '0 2px 8px rgba(0, 0, 0, 0.5)'
                          : '0 2px 4px rgba(0, 0, 0, 0.1)',
                        mb: 1,
                        textAlign: 'center',
                        letterSpacing: '0.02em',
                      }}
                    >
                      No Data Available
                    </Typography>
                    <Typography
                      sx={{
                        color: themeValues.noDataColor,
                        fontSize: { xs: '12px', sm: '13px', md: '14px' },
                        opacity: 0.75,
                        textAlign: 'center',
                        maxWidth: '240px',
                        fontWeight: '500',
                      }}
                    >
                      Please select a period to view chart data
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PieChart;
